import React, { useState } from 'react';
import LaunchModal from '../components/LaunchModal';

const SecurityPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="container mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Security & Infrastructure
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Enterprise-grade security powered by our hybrid cloud infrastructure, delivering unmatched scalability, cost-efficiency, and protection for your farm's critical data.
          </p>
        </div>

        {/* Infrastructure Architecture */}
        <section className="mb-16">
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
            <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
              Our Hybrid Cloud™ Architecture
            </h2>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-blue-400">Dedicated Hardware Foundation</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-3">•</span>
                    <span>Owned and managed dedicated servers colocated with Hybrid Hosting</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-3">•</span>
                    <span>Complete control over security configurations and data handling</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-3">•</span>
                    <span>Optimized performance for consistent farm operations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-3">•</span>
                    <span>Cost-effective baseline infrastructure with premium connectivity</span>
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-purple-400">Cloud Scaling Capability</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-3">•</span>
                    <span>Seamless scaling to Google Cloud during peak demand periods</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-3">•</span>
                    <span>Global infrastructure for international farm operations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-3">•</span>
                    <span>Enterprise-grade reliability and geographic redundancy</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 mr-3">•</span>
                    <span>Dynamic scaling with hybrid cloud orchestration</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="text-center">
              <p className="text-lg text-gray-300 italic">
                "Hybrid Cloud™ done right: dedicated performance with unlimited scalability"
              </p>
            </div>
          </div>
        </section>

        {/* Three Pillars */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Why Our Hybrid Cloud™ Approach Wins</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 text-center">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
                             <h3 className="text-xl font-semibold mb-3 text-blue-400">Top-Tier Scalability</h3>
               <p className="text-gray-300">
                 Start with dedicated hardware, scale infinitely with Google Cloud. Our Hybrid Cloud™ approach handles everything from small farms to enterprise operations without compromise.
               </p>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-green-400">Unmatched Pricing</h3>
              <p className="text-gray-300">
                Pay only for what you use. Fixed costs for baseline operations, variable costs only when you need extra capacity. No overprovisioning waste.
              </p>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
                             <h3 className="text-xl font-semibold mb-3 text-red-400">Maximum Security</h3>
               <p className="text-gray-300">
                 Complete control over your data with our managed hardware, plus enterprise-grade cloud security when needed. The ultimate defense strategy.
               </p>
            </div>
          </div>
        </section>

        {/* Data Locality Initiative */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-green-900/30 to-blue-900/30 backdrop-blur-sm rounded-2xl p-8 border border-green-700/50">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold mb-4 text-green-400">Local Data, Enhanced Security</h2>
              <div className="inline-flex items-center bg-green-500/20 px-4 py-2 rounded-full">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                <span className="text-green-300 font-medium">Active Development</span>
              </div>
            </div>
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-lg text-gray-300 mb-6">
                We're actively working with Hybrid Hosting to relocate our main data stores from Chicago to a 
                secure, state-of-the-art datacenter in <strong className="text-green-400">Lexington, Kentucky</strong>.
              </p>
              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-green-400 mb-2">Local & Secure</h3>
                  <p className="text-sm text-gray-300">Your farm data stays close to home in Kentucky</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-green-400 mb-2">Lower Latency</h3>
                  <p className="text-sm text-gray-300">Faster response times for regional users</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-green-400 mb-2">Enhanced Protection</h3>
                  <p className="text-sm text-gray-300">State-of-the-art security infrastructure</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Data Security */}
        <section className="mb-16">
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
            <h2 className="text-2xl font-semibold mb-6 text-blue-400">Enterprise-Grade Security</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-blue-400 mr-3">✓</span>
                  <span className="text-gray-300">End-to-end encryption for all data transmission</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-400 mr-3">✓</span>
                  <span className="text-gray-300">Multi-factor authentication with role-based access</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-400 mr-3">✓</span>
                  <span className="text-gray-300">Regular penetration testing and security audits</span>
                </li>
              </ul>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-blue-400 mr-3">✓</span>
                  <span className="text-gray-300">24/7 security monitoring and threat detection</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-400 mr-3">✓</span>
                  <span className="text-gray-300">Automated backup systems with geographic redundancy</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-400 mr-3">✓</span>
                  <span className="text-gray-300">Incident response team with rapid deployment</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Compliance & Standards */}
        <section className="mb-16">
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
            <h2 className="text-2xl font-semibold mb-6 text-purple-400">Compliance & Standards</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-purple-500/20 rounded-lg p-4 mb-3">
                  <h3 className="font-semibold text-purple-400">SOC 2 Type II</h3>
                </div>
                <p className="text-sm text-gray-400">Security, availability, and confidentiality controls</p>
              </div>
              <div className="text-center">
                <div className="bg-purple-500/20 rounded-lg p-4 mb-3">
                  <h3 className="font-semibold text-purple-400">PCI DSS</h3>
                </div>
                <p className="text-sm text-gray-400">Payment card industry data security standards</p>
              </div>
              <div className="text-center">
                <div className="bg-purple-500/20 rounded-lg p-4 mb-3">
                  <h3 className="font-semibold text-purple-400">GDPR Ready</h3>
                </div>
                <p className="text-sm text-gray-400">European data protection regulation compliance</p>
              </div>
            </div>
          </div>
        </section>

        {/* Infrastructure Reliability */}
        <section className="mb-16">
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
            <h2 className="text-2xl font-semibold mb-6 text-green-400">99.99% Uptime Guarantee</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold mb-4 text-green-400">Redundant Systems</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Multiple data center locations</li>
                  <li>• Automatic failover mechanisms</li>
                  <li>• Real-time health monitoring</li>
                  <li>• Load balancing across regions</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4 text-green-400">Backup Strategy</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Continuous data replication</li>
                  <li>• Point-in-time recovery options</li>
                  <li>• Cross-platform backup validation</li>
                  <li>• Disaster recovery protocols</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-8 border border-gray-700">
            <h2 className="text-2xl font-bold mb-4">Ready for the Future of Farm Management?</h2>
            <p className="text-gray-300 mb-6">
              BarnBills is launching soon. Be the first to know when our secure, scalable platform goes live.
            </p>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
            >
              Get Notified at Launch
            </button>
          </div>
        </section>
      </div>

      <LaunchModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default SecurityPage; 